import type { DocumentTypeDefinition, SupportedDocumentType } from '@/features/case-workspace/types'
import { CBP_7501_SECTIONS } from '@/features/case-workspace/cbp7501'
import { COMMERCIAL_INVOICE_SECTIONS } from '@/features/case-workspace/invoiceTemplate'
import { MANIFEST_1302_SECTIONS } from '@/features/case-workspace/manifest1302'

export const DOCUMENT_TYPE_ORDER: SupportedDocumentType[] = [
  'cbp_7501',
  'manifest',
  'commercial_invoice',
  'permit_license',
]

export const DOCUMENT_DEFINITIONS: Record<SupportedDocumentType, DocumentTypeDefinition> = {
  cbp_7501: {
    type: 'cbp_7501',
    label: 'CBP Form 7501',
    shortDescription: 'Entry summary record covering official header blocks, line items, totals, declaration, and filer metadata.',
    requiredForReview: true,
    iconLabel: '7501',
    schemaVersion: 'cbp-form-7501-2026-02',
    aceReferenceNote: 'Aligned to the local CBP Form 7501 PDF and instructions. Keep enum lists and formatting rules in sync with future CBP revisions.',
    sections: CBP_7501_SECTIONS,
  },
  manifest: {
    type: 'manifest',
    label: 'Manifest',
    shortDescription: 'CBP Form 1302 cargo declaration record covering manifest intake source, vessel header data, ports, bill-level cargo details, reconciliation, and audit history.',
    requiredForReview: true,
    iconLabel: 'MNF',
    schemaVersion: 'cbp-form-1302-2026-04',
    aceReferenceNote: 'Aligned to the CBP Form 1302 page and official PDF link on cbp.gov. Keep the cargo declaration field model, intake-source options, and routing terminology in sync with future CBP updates.',
    sections: MANIFEST_1302_SECTIONS,
  },
  commercial_invoice: {
    type: 'commercial_invoice',
    label: 'Commercial Invoice',
    shortDescription: 'Normalized customs invoice review record covering invoice identity, parties, routing, merchandise lines, valuation, certifications, and audit history.',
    requiredForReview: true,
    iconLabel: 'INV',
    schemaVersion: 'invoice-review-template-2026-04',
    aceReferenceNote: 'Aligned to 19 CFR 141.83 through 141.89, especially 19 CFR 141.86. Keep the normalized field registry and reconciliation mappings in sync with future regulatory or ACE-aligned guidance.',
    sections: COMMERCIAL_INVOICE_SECTIONS,
  },
  permit_license: {
    type: 'permit_license',
    label: 'Permit / License',
    shortDescription: 'PGA related authorizations tied to the shipment or covered commodities.',
    requiredForReview: false,
    iconLabel: 'PGA',
    schemaVersion: 'ace-draft-v1',
    aceReferenceNote: 'TODO: map agency specific permit and license fields once jurisdictional requirements are confirmed.',
    sections: [
      {
        id: 'agency',
        title: 'Agency information',
        fields: [
          {
            key: 'agency_name',
            label: 'Agency name',
            type: 'text',
            aceMappingId: 'permit.agency_name',
          },
          {
            key: 'permit_number',
            label: 'Permit or license number',
            type: 'text',
            required: true,
            aceMappingId: 'permit.permit_number',
            validation: [{ type: 'required', message: 'Permit or license number is required.' }],
          },
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            aceMappingId: 'permit.status',
            options: [
              { value: 'requested', label: 'Requested' },
              { value: 'active', label: 'Active' },
              { value: 'expired', label: 'Expired' },
            ],
          },
        ],
      },
      {
        id: 'coverage',
        title: 'Coverage',
        fields: [
          {
            key: 'covered_commodity',
            label: 'Covered commodity',
            type: 'text',
            aceMappingId: 'permit.covered_commodity',
          },
          {
            key: 'expiration_date',
            label: 'Expiration date',
            type: 'date',
            aceMappingId: 'permit.expiration_date',
          },
          {
            key: 'notes',
            label: 'Notes',
            type: 'textarea',
            aceMappingId: 'permit.notes',
          },
        ],
      },
    ],
  },
}
